export function onImageChange(options: {
  delete: (key: string) => void;
  get: (key: string) => string | undefined;
  set: (key: string, value: string) => void;
}) {
  function getSaturation(r: number, g: number, b: number): number {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    if (max === min) return 0;
    const l = (max + min) / 2;
    const d = max - min;
    return l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }

  function getRelativeLuminance(r: number, g: number, b: number): number {
    const linearize = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    return 0.2126 * linearize(r / 255) + 0.7152 * linearize(g / 255) + 0.0722 * linearize(b / 255);
  }

  function getDarkVibrantColor(img: HTMLImageElement): string | null {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Accumulators: dark-vibrant (preferred), dark-any (fallback), all pixels (final fallback)
    let dvR = 0,
      dvG = 0,
      dvB = 0,
      dvCount = 0;
    let dkR = 0,
      dkG = 0,
      dkB = 0,
      dkCount = 0;
    let allR = 0,
      allG = 0,
      allB = 0,
      allCount = 0;

    for (let i = 0; i < data.length; i += 80) {
      const alpha = data[i + 3] ?? 0;
      if (alpha < 128) continue;

      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      const lum = getRelativeLuminance(r, g, b);
      const s = getSaturation(r, g, b);

      allR += r;
      allG += g;
      allB += b;
      allCount++;

      if (lum <= 0.179) {
        dkR += r;
        dkG += g;
        dkB += b;
        dkCount++;

        if (s >= 0.35) {
          dvR += r;
          dvG += g;
          dvB += b;
          dvCount++;
        }
      }
    }

    // Prefer dark-vibrant, fall back to any dark, then overall average
    let fr, fg, fb, fc;
    if (dvCount > 0) {
      fr = dvR;
      fg = dvG;
      fb = dvB;
      fc = dvCount;
    } else if (dkCount > 0) {
      fr = dkR;
      fg = dkG;
      fb = dkB;
      fc = dkCount;
    } else if (allCount > 0) {
      fr = allR;
      fg = allG;
      fb = allB;
      fc = allCount;
    } else {
      return null;
    }

    return "rgb(" + Math.round(fr / fc) + ", " + Math.round(fg / fc) + ", " + Math.round(fb / fc) + ")";
  }

  // Every responsive image gets its own dominant colour, because the crops can differ.
  // Builder invokes this handler with the full options bag, so all fields are refreshed at once.
  const imageFields = [
    { imageField: "imageUrl", colorField: "dominantColor" },
    { imageField: "tabletImageUrl", colorField: "tabletDominantColor" },
    { imageField: "desktopImageUrl", colorField: "desktopDominantColor" },
  ];

  imageFields.forEach((field) => {
    const url = options.get(field.imageField);

    if (!url) {
      options.set(field.colorField, "");
      return;
    }

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;

    img.onload = () => {
      const color = getDarkVibrantColor(img);
      if (color) {
        options.set(field.colorField, color);
      }
    };
  });
}
